import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Trophy, Users2, Calendar, Check, X, Shuffle, Plus, Trash2, UserPlus, Crown } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLoading } from "../context/LoadingContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

const CATEGORY_META = {
  normal: { label: "Normal", icon: Calendar, color: "#8B5CF6" },
  competition: { label: "Competition", icon: Trophy, color: "#F59E0B" },
  teamwork: { label: "Teamwork", icon: Users2, color: "#10B981" },
};

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const { withLoading } = useLoading();

  const [event, setEvent] = useState(null);
  const [candidates, setCandidates] = useState([]); // staff + customers
  const [addOpen, setAddOpen] = useState(false);
  const [absentModal, setAbsentModal] = useState(null); // attendee
  const [reason, setReason] = useState("");
  const [newTeam, setNewTeam] = useState("");

  const load = async () => { const r = await api.get(`/events/${id}`); setEvent(r.data); };

  const loadCandidates = async () => {
    const [s, c] = await Promise.all([api.get("/staff"), api.get("/customers")]);
    const list = [
      ...s.data.map((x) => ({ user_id: x.id, user_type: "staff", name: `${x.first_name} ${x.last_name}`, avatar: x.avatar_url || x.discord_avatar })),
      ...c.data.map((x) => ({ user_id: x.id, user_type: "customer", name: x.discord_username || "Customer", avatar: x.discord_avatar })),
    ];
    setCandidates(list);
  };

  useEffect(() => { load(); loadCandidates(); }, [id]);

  if (!event) return <div className="flex h-64 items-center justify-center text-slate-500">Loading event...</div>;
  const meta = CATEGORY_META[event.category] || CATEGORY_META.normal;

  const setStatus = async (attendee, status) => {
    if (status === "absent") { setAbsentModal(attendee); setReason(attendee.reason || ""); return; }
    await withLoading(api.post(`/events/${id}/attendance`, { user_id: attendee.user_id, status, reason: "" }), "Saving attendance");
    load();
  };

  const confirmAbsent = async () => {
    await withLoading(api.post(`/events/${id}/attendance`, { user_id: absentModal.user_id, status: "absent", reason }), "Saving attendance");
    setAbsentModal(null); setReason(""); toast.success("Marked absent"); load();
  };

  const addAttendee = async (cand) => {
    await withLoading(api.post(`/events/${id}/attendees`, { user_id: cand.user_id, user_type: cand.user_type }), "Adding attendee");
    load();
  };
  const removeAttendee = async (uid) => { await withLoading(api.delete(`/events/${id}/attendees/${uid}`), "Removing"); load(); };

  const setWinner = async (uid) => { await withLoading(api.post(`/events/${id}/winner`, { winner_user_id: uid }), "Crowning winner"); toast.success("Winner set!"); load(); };

  const addTeam = async () => { if (!newTeam) return; await withLoading(api.post(`/events/${id}/teams`, { name: newTeam }), "Creating team"); setNewTeam(""); load(); };
  const delTeam = async (tid) => { await withLoading(api.delete(`/events/${id}/teams/${tid}`), "Deleting team"); load(); };
  const assignTeam = async (uid, tid) => { await withLoading(api.post(`/events/${id}/assign-team`, { user_id: uid, team_id: tid }), "Assigning"); load(); };
  const randomize = async () => { await withLoading(api.post(`/events/${id}/randomize-teams`), "Randomizing teams"); toast.success("Teams randomized!"); load(); };

  const setEventStatus = async (status) => { await withLoading(api.put(`/events/${id}`, { status }), "Updating"); load(); };
  const deleteEvent = async () => { await withLoading(api.delete(`/events/${id}`), "Deleting event"); navigate("/events"); };

  const attendeeIds = new Set(event.attendees.map((a) => a.user_id));
  const available = candidates.filter((c) => !attendeeIds.has(c.user_id));
  const canManage = hasPerm("manage_events");
  const canAttend = hasPerm("take_attendance");

  return (
    <div>
      <button onClick={() => navigate("/events")} data-testid="back-to-events" className="mb-4 flex items-center gap-1 text-sm text-slate-400 hover:text-purple-400"><ArrowLeft className="h-4 w-4" /> Back to Events</button>

      <div className="card-glass mb-6 rounded-2xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="rounded-xl p-3" style={{ background: `${meta.color}22`, color: meta.color }}><meta.icon className="h-7 w-7" /></span>
            <div>
              <h1 className="font-display text-3xl font-black text-white">{event.title}</h1>
              <p className="text-sm text-slate-400">{new Date(event.scheduled_at).toLocaleString()}</p>
              {event.description && <p className="mt-1 max-w-xl text-sm text-slate-500">{event.description}</p>}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full px-3 py-1 text-xs font-medium capitalize" style={{ background: `${meta.color}18`, color: meta.color }}>{meta.label}</span>
            {event.event_type && <span className="rounded-full px-3 py-1 text-xs font-medium" style={{ background: `${event.event_type.color}18`, color: event.event_type.color }}>{event.event_type.name}</span>}
            {canManage && (
              <Select value={event.status} onValueChange={setEventStatus}>
                <SelectTrigger data-testid="event-status-select" className="h-8 w-36 bg-slate-900/60 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="border-slate-800 bg-[#181D2A] text-slate-100">
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="ongoing">Ongoing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            )}
            {canManage && <Button data-testid="delete-event-btn" size="sm" variant="ghost" onClick={deleteEvent} className="text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></Button>}
          </div>
        </div>
      </div>

      {/* TEAMWORK panel */}
      {event.category === "teamwork" && (
        <div className="card-glass mb-6 rounded-2xl p-6" data-testid="teamwork-panel">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-white"><Users2 className="h-5 w-5 text-emerald-400" /> Teams</h2>
            {canManage && (
              <div className="flex items-center gap-2">
                <Input data-testid="new-team-name" value={newTeam} onChange={(e) => setNewTeam(e.target.value)} placeholder="Team name" className="h-9 w-40 bg-slate-900/60" />
                <Button data-testid="add-team-btn" size="sm" onClick={addTeam} className="gap-1 bg-emerald-600 hover:bg-emerald-500"><Plus className="h-4 w-4" /> Team</Button>
                <Button data-testid="randomize-teams-btn" size="sm" onClick={randomize} className="gap-1 bg-purple-600 hover:bg-purple-500"><Shuffle className="h-4 w-4" /> Randomize</Button>
              </div>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {event.teams.length === 0 && <p className="text-sm text-slate-500">No teams yet. Create teams, then assign or randomize members.</p>}
            {event.teams.map((t) => (
              <div key={t.id} className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4" data-testid={`team-${t.id}`}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-semibold text-emerald-300">{t.name}</span>
                  {canManage && <Button size="sm" variant="ghost" onClick={() => delTeam(t.id)} className="h-6 text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>}
                </div>
                <div className="space-y-1.5">
                  {event.attendees.filter((a) => a.team_id === t.id).map((a) => (
                    <div key={a.user_id} className="flex items-center gap-2 text-sm text-slate-200"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {a.name}</div>
                  ))}
                  {event.attendees.filter((a) => a.team_id === t.id).length === 0 && <p className="text-xs text-slate-600">Empty</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ATTENDEES */}
      <div className="card-glass rounded-2xl p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-white">Attendees ({event.attendees.length})</h2>
          {canManage && <Button data-testid="add-attendee-btn" size="sm" onClick={() => setAddOpen(true)} className="gap-1 bg-purple-600 hover:bg-purple-500"><UserPlus className="h-4 w-4" /> Add Attendee</Button>}
        </div>

        <div className="space-y-2">
          {event.attendees.length === 0 && <p className="py-6 text-center text-sm text-slate-500">No attendees yet.</p>}
          {event.attendees.map((a) => {
            const isWinner = event.winner_user_id === a.user_id;
            return (
              <div key={a.user_id} className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 ${isWinner ? "border-amber-500/40 bg-amber-500/5" : "border-slate-800/60 bg-slate-900/40"}`} data-testid={`attendee-${a.user_id}`}>
                <Avatar className="h-9 w-9 border border-slate-700"><AvatarImage src={a.avatar} /><AvatarFallback className="bg-purple-600/30 text-xs text-purple-200">{a.name.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1 font-medium text-white">{a.name} {isWinner && <Crown className="h-4 w-4 text-amber-400" />}</p>
                  <p className="text-xs capitalize text-slate-500">{a.user_type}{a.status === "absent" && a.reason ? ` · Reason: ${a.reason}` : ""}</p>
                </div>

                {event.category === "teamwork" && canManage && (
                  <Select value={a.team_id || "none"} onValueChange={(v) => assignTeam(a.user_id, v)}>
                    <SelectTrigger data-testid={`assign-team-${a.user_id}`} className="h-8 w-32 bg-slate-900/60 text-xs"><SelectValue placeholder="Team" /></SelectTrigger>
                    <SelectContent className="border-slate-800 bg-[#181D2A] text-slate-100">
                      <SelectItem value="none">No team</SelectItem>
                      {event.teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}

                {/* attendance toggle */}
                {canAttend && (
                  <div className="flex items-center gap-1.5">
                    <Button data-testid={`present-${a.user_id}`} size="sm" onClick={() => setStatus(a, "present")} className={`h-8 gap-1 ${a.status === "present" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-slate-800 text-slate-300 hover:bg-emerald-600/40"}`}><Check className="h-3.5 w-3.5" /> Present</Button>
                    <Button data-testid={`absent-${a.user_id}`} size="sm" onClick={() => setStatus(a, "absent")} className={`h-8 gap-1 ${a.status === "absent" ? "bg-red-600 hover:bg-red-500" : "bg-slate-800 text-slate-300 hover:bg-red-600/40"}`}><X className="h-3.5 w-3.5" /> Absent</Button>
                  </div>
                )}
                {!canAttend && (
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize ${a.status === "present" ? "bg-emerald-500/10 text-emerald-400" : a.status === "absent" ? "bg-red-500/10 text-red-400" : "bg-slate-700/40 text-slate-400"}`}>{a.status}</span>
                )}

                {event.category === "competition" && canManage && (
                  <Button data-testid={`winner-${a.user_id}`} size="sm" variant={isWinner ? "default" : "secondary"} onClick={() => setWinner(a.user_id)} className={`h-8 gap-1 ${isWinner ? "bg-amber-600 hover:bg-amber-500" : ""}`}><Trophy className="h-3.5 w-3.5" /> {isWinner ? "Winner" : "Set Winner"}</Button>
                )}

                {canManage && <Button size="sm" variant="ghost" onClick={() => removeAttendee(a.user_id)} className="h-8 text-slate-500 hover:bg-red-500/10 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add attendee dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="border-slate-800 bg-[#121620] text-slate-100" data-testid="add-attendee-dialog">
          <DialogHeader><DialogTitle>Add Attendees</DialogTitle></DialogHeader>
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {available.length === 0 && <p className="text-sm text-slate-500">Everyone is already an attendee.</p>}
            {available.map((c) => (
              <div key={c.user_id} className="flex items-center justify-between rounded-lg border border-slate-800/60 bg-slate-900/40 px-3 py-2">
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8"><AvatarImage src={c.avatar} /><AvatarFallback className="bg-purple-600/30 text-xs text-purple-200">{c.name.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                  <div><p className="text-sm text-white">{c.name}</p><p className="text-xs capitalize text-slate-500">{c.user_type}</p></div>
                </div>
                <Button data-testid={`add-candidate-${c.user_id}`} size="sm" onClick={() => addAttendee(c)} className="gap-1 bg-purple-600 hover:bg-purple-500"><Plus className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="secondary" onClick={() => setAddOpen(false)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Absent reason modal */}
      <Dialog open={!!absentModal} onOpenChange={(o) => !o && setAbsentModal(null)}>
        <DialogContent className="border-slate-800 bg-[#121620] text-slate-100" data-testid="absent-reason-dialog">
          <DialogHeader><DialogTitle>Mark {absentModal?.name} Absent</DialogTitle></DialogHeader>
          <div><Label className="text-xs">Reason for absence</Label><Textarea data-testid="absent-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Sick, no-show, notified in advance..." className="mt-1 bg-slate-900/60" /></div>
          <DialogFooter><Button data-testid="confirm-absent-btn" onClick={confirmAbsent} className="bg-red-600 hover:bg-red-500">Mark Absent</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
